import { DropdownMenuItem, FAIcon, ModuleWrapper } from '@/components'
import type { GetSpecificConditionsData } from '@/modules/activities'
import { GET_SPECIFIC_CONDITIONS } from '@/modules/activities'
import type { ExportForm } from '@/modules/exports'
import { PageType } from '@/modules/common/consts'
import { format } from '@/utils/date-fns'
import {
  useBreadcrumbs,
  useOnErrorAuto,
  useParam,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { useMutation, useQuery } from '@apollo/client'
import { Loader, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO, parseISO } from 'date-fns'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import {
  INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
  INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
  INFRINGEMENT_QUERY,
  InfringementDetailV1,
  PATCH_INFRINGEMENT_MUTATION,
  useInfringementsMutations,
} from '..'
import { useInfringementCategoryDescription } from '../category-descriptions'
import type { DetailField } from '../components/detail/detail-header'
import {
  DetailPill,
  InfringementDetailHeader,
} from '../components/detail/detail-header'
import {
  type FerrySpecificConditionType,
  getFerryConditionsWindow,
} from '../ferry-conditions'
import { ExportInfringementsModal } from '../components/ExportInfringementsModal'
import type {
  FollowUpChanges,
  FollowUpInfringement,
} from '../components/infringement-follow-up'
import {
  InfringementActionLogCard,
  InfringementActions,
  infringementActionsPlacement,
  InfringementAttachmentsCard,
  InfringementFollowUpBlocks,
  InfringementStatusPills,
} from '../components/infringement-follow-up'
import type { InfringementQueryInfringement } from '../types.graphl'
import {
  useInfringementSeverityFunctions,
  useSeverityBackground,
} from '../util'
import { getNodes } from '@/utils/graphql'

const FERRY_SPECIFIC_CONDITION_TYPES: ReadonlySet<string> =
  new Set<FerrySpecificConditionType>(['FERRY', 'FERRY_START', 'FERRY_END'])

interface InfringementProps {
  listPath?: string
}

export const Infringement: React.FC<InfringementProps> = ({
  listPath = '/infringements',
}) => {
  const translations = useTranslate({
    infringement: 'common.infringement',
    loading: 'common.loading-ellipsis',
    ferryEvents: 'infringements.ferry-events',
    ferryEvent: 'infringements.ferry-event',
    ferryStart: 'infringements.ferry-start',
    ferryEnd: 'infringements.ferry-end',
  })

  const navigate = useNavigate()
  const infringementId = useParam('infringementId')
  const { setOverride } = useBreadcrumbs()
  const onErrorAuto = useOnErrorAuto()

  const { data, loading: queryLoading } = useQuery(INFRINGEMENT_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      id: infringementId,
      activitiesExtendBeforeMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_BEFORE_MINUTES,
      activitiesExtendAfterMinutes:
        INFRINGEMENT_ACTIVITIES_EXTEND_AFTER_MINUTES,
    },
    onCompleted(data) {
      if (!data.infringement) void navigate(listPath)

      const { infringement } = data
      setOverride(
        infringement.id,
        format(new Date(infringement.createdAt), 'PP')
      )
    },
    onError: onErrorAuto(),
  })

  useEffect(() => {
    if (data) return
    setOverride(infringementId, translations.loading)
  }, [data, infringementId, setOverride, translations.loading])

  const infringement = data?.infringement
  const userId = infringement?.user.id

  const conditionsWindow = useMemo(
    () => (infringement ? getFerryConditionsWindow(infringement) : null),
    [infringement]
  )

  const { data: specificConditionsData } = useQuery<GetSpecificConditionsData>(
    GET_SPECIFIC_CONDITIONS,
    {
      skip: !userId || !conditionsWindow,
      variables: {
        userId,
        dateStart: conditionsWindow?.dateStart,
        dateEnd: conditionsWindow?.dateEnd,
      },
      onError: onErrorAuto(),
    }
  )
  const ferrySpecificConditions = useMemo(
    () =>
      getNodes(specificConditionsData?.allSpecificConditions)
        .filter(
          (
            condition
          ): condition is typeof condition & {
            type: FerrySpecificConditionType
          } => FERRY_SPECIFIC_CONDITION_TYPES.has(condition.type)
        )
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
    [specificConditionsData?.allSpecificConditions]
  )

  if (!infringement) {
    return (
      <ModuleWrapper
        title={translations.infringement}
        page={PageType.INFRINGEMENTS}
      >
        {queryLoading && <Loader.Spinner />}
      </ModuleWrapper>
    )
  }

  return (
    <ModuleWrapper
      title={translations.infringement}
      page={PageType.INFRINGEMENTS}
      {...infringementActionsPlacement(
        <DrivingRestActions infringement={infringement} />
      )}
    >
      <div className="w-full space-y-6">
        <InfringementDetail infringement={infringement} />

        <InfringementDetailV1
          infringement={infringement}
          ferrySpecificConditions={ferrySpecificConditions}
          showTimes={false}
        />

        {ferrySpecificConditions.length > 0 && (
          <section className="overflow-hidden rounded-2xl bg-white shadow-card">
            <header className="border-b border-stone-200 px-5 py-4 sm:px-6">
              <h3 className="text-lg font-semibold text-stone-900">
                {translations.ferryEvents}
              </h3>
            </header>
            <ul className="divide-y divide-stone-200">
              {ferrySpecificConditions.map(node => (
                <li key={node.id} className="px-5 py-3 text-sm sm:px-6">
                  <span className="text-stone-500">
                    {format(new Date(node.timestamp), 'dd.MM.yyyy HH:mm')}:
                  </span>{' '}
                  <span className="text-stone-700">
                    {
                      {
                        FERRY: translations.ferryEvent,
                        FERRY_START: translations.ferryStart,
                        FERRY_END: translations.ferryEnd,
                      }[node.type]
                    }
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <InfringementActionLogCard
          actionLogs={getNodes(infringement.actionLogs)}
        />
      </div>
    </ModuleWrapper>
  )
}

/** The infringement's facts and follow-up, in the Absence page's layout shared
 * with the other infringement modules. The rule's description is the
 * subtitle, and the timeline below carries the limit and recorded values. */
const InfringementDetail: React.FC<{
  infringement: InfringementQueryInfringement
}> = ({ infringement }) => {
  const { patch } = usePatchInfringement(infringement.id)
  const translations = useTranslate({
    eyebrow: 'infringements.source.driving-rest',
    timestamp: 'common.timestamp',
    period: 'common.period',
    article: 'common.article',
    legalBasis: 'infringements.legal-basis',
    archivedBasis: 'infringements.archived-basis',
    archivedDate: 'infringements.archived-date',
  })
  const categoryDescription = useInfringementCategoryDescription()
  const getSeverityBackgroundColor = useSeverityBackground()
  const { getSeverityTranslation } = useInfringementSeverityFunctions()

  const category = infringement.infringementCategory
  const articles = (category.legalBases?.edges ?? [])
    .map(edge => edge?.node?.article ?? '')
    .filter(Boolean)

  const fields: DetailField[] = [
    {
      label: translations.timestamp,
      value: format(
        parseISO(infringement.infringementTimestamp),
        'dd.MM.yyyy HH:mm'
      ),
    },
  ]
  if (infringement.archivedBasis) {
    fields.push({
      label: translations.archivedBasis,
      value: infringement.archivedBasis,
    })
  }
  const info: DetailField[] = [
    {
      label: translations.period,
      value: `${format(
        parseISO(infringement.periodStartDate),
        'PP HH:mm'
      )} - ${format(parseISO(infringement.periodEndDate), 'PP HH:mm')}`,
    },
    {
      label: translations.legalBasis,
      value: articles.length
        ? `${translations.article}: ${articles.join(', ')}`
        : '-',
    },
  ]
  if (infringement.archivedAt) {
    info.push({
      label: translations.archivedDate,
      value: format(parseISO(infringement.archivedAt), 'PP HH:mm'),
    })
  }

  const followUpInfringement = toFollowUpInfringement(infringement)
  const onPatch = (changes: FollowUpChanges) => patch({ ...changes })

  return (
    <>
      <InfringementDetailHeader
        eyebrow={translations.eyebrow}
        title={infringement.user.fullName}
        rule={`${category.identifier} · ${categoryDescription(
          category.identifier,
          category.description
        )}`}
        pills={
          <>
            <InfringementStatusPills infringement={followUpInfringement} />
            <DetailPill
              tone="stone"
              dotColor={getSeverityBackgroundColor(category.severity)}
            >
              {getSeverityTranslation(category.severity)}
            </DetailPill>
          </>
        }
        fields={fields}
        info={info}
      >
        <InfringementFollowUpBlocks
          infringement={followUpInfringement}
          onPatch={onPatch}
        />
      </InfringementDetailHeader>

      <InfringementAttachmentsCard
        infringement={followUpInfringement}
        onPatch={onPatch}
      />
    </>
  )
}

function toFollowUpInfringement(
  infringement: InfringementQueryInfringement
): FollowUpInfringement {
  return {
    id: infringement.id,
    user: infringement.user,
    solution: infringement.solution,
    issueReason: infringement.issueReason,
    leadersComment: infringement.leadersComment,
    archivedAt: infringement.archivedAt,
    closedAt: infringement.closedAt,
    attachments: getNodes(infringement.attachments),
    actionLogs: [],
  }
}

function usePatchInfringement(id: string) {
  const onErrorAuto = useOnErrorAuto()
  const [patchInfringement, { loading }] = useMutation(
    PATCH_INFRINGEMENT_MUTATION,
    {
      awaitRefetchQueries: true,
      refetchQueries: ['Infringement', 'AllCombinedInfringements', 'AllIssues'],
      onError: onErrorAuto(),
    }
  )
  return {
    loading,
    patch: (input: Record<string, unknown>) =>
      patchInfringement({ variables: { id, input } }),
  }
}

/** The page header's actions: close and the menu with archiving and the
 * export. */
const DrivingRestActions: React.FC<{
  infringement: InfringementQueryInfringement
}> = ({ infringement }) => {
  const translations = useTranslate({ export: 'common.export' })
  const { exportInfringement, loading: exporting } = useInfringementsMutations()
  const addPrompt = usePrompt()
  const me = useUser()
  const { hasPermissions } = usePermissions()
  const canExport = hasPermissions(PERMISSIONS.companies.export.exportReports)
  const { patch, loading: patching } = usePatchInfringement(infringement.id)

  async function handleExport() {
    const { data } = await addPrompt<ExportForm | null>(resolve => (
      <ExportInfringementsModal onSubmit={resolve} />
    ))
    if (!data) return

    void exportInfringement({
      variables: {
        infringementId: infringement.id,
        exportOption: data.exportOption,
        recipientId: me.id,
        recipientEmails:
          data.recipients.length === 0 ? [me.id] : data.recipients,
      },
    })
  }

  return (
    <InfringementActions
      closed={infringement.closedAt !== null}
      archived={infringement.archivedAt !== null}
      busy={patching || exporting}
      onSetClosed={closed =>
        patch({
          closedAt: closed ? formatISO(new Date()) : null,
          closedBy: me.id,
        })
      }
      onSetArchived={(archived, archivedBasis) =>
        patch({
          archivedBy: me.id,
          archivedAt: archived ? formatISO(new Date()) : null,
          ...(archived && { archivedBasis }),
        })
      }
      menuItems={
        canExport && (
          <DropdownMenuItem onClick={() => void handleExport()}>
            <FAIcon icon="inbox-in" fixedWidth />
            {translations.export}
          </DropdownMenuItem>
        )
      }
    />
  )
}
